import {
  useInfiniteQuery,
  keepPreviousData,
  QueryFunction,
  QueryKey,
} from '@tanstack/react-query'

import { PromoCardConfiguration, CollectionProductsPage } from '../types'

import { UseProductsQueryOptions } from './types'
import { useAlgoliaDataExtension } from '@/features/product/hooks/useAlgoliaDataExtension/useAlgoliaDataExtension'
import { useMemo } from 'react'

/**
 * Gets the keys of the promo-cards on a given page.
 * @param promoCards The complete set of promo-cards.
 * @param page The page to find promo-cards for.
 * @param pageSize The size of each page.
 * @returns The keys of the promo-cards for the given page.
 */
const getPromoCardsForPage = (
  promoCards: { [key: number]: PromoCardConfiguration },
  page: number,
  pageSize: number,
) =>
  Object.keys(promoCards)
    .map((key) => parseInt(key, 10))
    .filter((key) => {
      const lowerBound = page * pageSize
      const upperBound = lowerBound + (pageSize - 1)
      return key >= lowerBound && key <= upperBound
    })

export const useProductsQueryHandler =
  ({
    promoCards,
    targetPageSize,
    queryFn,
    collectionId,
    filterValues,
    useSwatches,
  }: UseProductsQueryOptions): QueryFunction<
    CollectionProductsPage,
    QueryKey,
    number | undefined
  > =>
  async ({ pageParam = 0, ...args }) => {
    // We use a page=1 query param, but the first page we want to use is 0 (i.e. we want an offset of 0 for page 1).
    const page = Math.max(pageParam - 1, 0)

    // Keep track of the promo-card offsets for each page.
    const promoCardOffsets = []
    for (let i = 0; i <= page; i++) {
      const pagePromoCards = getPromoCardsForPage(promoCards, i, targetPageSize)

      // For each of the promo cards on the page reduce the count by the
      // number of 'slots' each card takes up.
      const promoCardOffset = pagePromoCards.reduce(
        (prev, key) => prev + (promoCards[key].layout === '1x1' ? 1 : 2),
        0,
      )

      promoCardOffsets.push(promoCardOffset)
    }

    // Calculate the accumulated offset for upto (not including) the current page.
    const accumulatedOffset = promoCardOffsets
      .slice(0, page)
      .reduce((prev, curr) => prev + curr, 0)

    // The promo card offsets for this page only. We use this to reduce
    // the amount of products we fetch on this page.
    const pageOffset = promoCardOffsets[page] ?? 0

    // The start offset for the current page, i.e. page 1 = 0, page 2 = 24 etc.
    // This takes into account the promo-cards on the previous pages.
    //
    // e.g. a 2x1 promo-card is on page 1, so we've only fetched 22 products on page 1,
    // we want to fetch 24 products on page 2, but we don't want to miss the 2 we missed from page 1.
    const startOffset = targetPageSize * page - accumulatedOffset
    const length = targetPageSize - pageOffset

    const filters = {
      collections: collectionId,
      ...filterValues?.filters,
    }

    const sort = filterValues?.sort ?? null

    const response = await queryFn({
      ...args,
      length,
      offset: startOffset,
      filters,
      sort,
      useSwatches,
    })

    // Check the total count against the end offset to see if we can fetch more.
    // We add 2 to normalise the page numbers to 1-indexed for consumers of this hook.
    const nextCursor =
      response.totalCount > response.endOffset ? page + 2 : undefined

    const prevCursor = page >= 1 ? page : undefined

    return {
      ...response,
      nextCursor,
      prevCursor,
    }
  }

/**
 * Query for configuration wrapper for fetching the products of the collection.
 * The queryFn will calculate how many promo-cards are on the current page and
 * reduce the count to fetch by the amount of grid-cells they take up.
 */
export const useProductsQuery = ({
  collectionId,
  promoCards,
  targetPageSize,
  initialPageParam,
  queryFn,
  filterValues,
  useSwatches,
  ...queryOptions
}: UseProductsQueryOptions) => {
  const { data: { pages = [] } = {}, ...result } = useInfiniteQuery({
    queryKey: ['collection', collectionId, { ...filterValues }],
    queryFn: useProductsQueryHandler({
      promoCards,
      targetPageSize,
      queryFn,
      collectionId,
      filterValues,
      useSwatches,
    }),
    placeholderData: keepPreviousData,
    initialPageParam: initialPageParam ?? 0,
    getNextPageParam: ({ nextCursor }) => nextCursor,
    getPreviousPageParam: ({ prevCursor }) => prevCursor,
    ...queryOptions,
  })

  return { data: { pages }, ...result }
}
